import {useMemo, useState} from 'react';
import {Search} from 'lucide-react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  InputGroupAddon
} from '@kroll-resolver-crisp/ui-components-react';
import {useLanguages, useLanguageSearch} from '@/api/hooks';
import {useDebounce} from '@/lib/useDebounce';
import {cn} from '@/lib/utils';

type Props = {
  readonly value: string;
  readonly onChange: (name: string) => void;
  readonly className?: string;
};

// Base UI's combobox re-highlights the first item whenever the item set changes only in its
// "always" mode; plain `true` highlights on a keystroke, and the server results this list is
// filtered by arrive after the debounce, when there is no keystroke left to highlight on. The
// implementation forwards the value untouched and accepts "always", but @base-ui/react 1.8.0's
// public `Combobox` type still narrows it to a boolean, hence the cast.
const HIGHLIGHT_TOP_RESULT = 'always' as unknown as boolean;

/**
 Single-select language combobox whose search reaches the full ISO 639-3 set, not just the
 default-visible subset `useLanguages` preloads — same data pattern as `LanguagePicker` in
 classify/EnrichmentForm.tsx, adapted for a single value instead of a multi-select.

 The combobox itself is the design system's: it owns the listbox/option roles, the highlight, the
 keyboard model and a portalled popup that no dialog can clip. Clearing is its clear button, or
 emptying the input, which in single-select unsets the value — so there is no separate "Unset" row,
 and the top result is highlighted on open so "type a term, press Enter" selects the match.
 */
export function LanguageSelect({value, onChange, className}: Props) {
  // Only what the analyst has typed since opening. The combobox also writes the selected name
  // back into the input (on selection and when the popup closes), and treating that as a search
  // would reopen onto a list filtered to the one language already chosen.
  const [search, setSearch] = useState('');
  const {data: languages} = useLanguages();
  const debouncedSearch = useDebounce(search, 300);
  const {data: matches, isFetching} = useLanguageSearch(debouncedSearch);

  const trimmed = search.trim();
  const isSearching = trimmed.length > 0;
  // The debounce means a server search is "in flight" for up to 300ms before `useLanguageSearch`
  // even issues the request — without this, that gap renders as a "No results" flash.
  const awaitingDebounce = trimmed.length >= 2 && debouncedSearch.trim() !== trimmed;
  // The names are the item values, because a name is what the contract carries. Memoised so the
  // list's identity only changes when its content does: the combobox syncs its input to the
  // selected label whenever the items change, and a fresh array every render would do that under
  // a half-typed search.
  const items = useMemo(() => {
    const matched = isSearching
      ? trimmed.length >= 2
        ? (matches ?? [])
        : (languages ?? []).filter((l) => l.name.toLowerCase().includes(trimmed.toLowerCase()))
      : (languages ?? []);
    return matched.map((item) => item.name);
  }, [isSearching, trimmed, matches, languages]);

  return (
    <Combobox
      items={items}
      // The list is already filtered above - locally for a single character, by the server for
      // more - so the combobox's own substring filter must not run a second time over it.
      filter={null}
      value={value || null}
      autoHighlight={HIGHLIGHT_TOP_RESULT}
      onValueChange={(next) => {
        onChange(next ?? '');
      }}
      onInputValueChange={(next, details) => {
        setSearch(details.reason === 'input-change' ? next : '');
      }}>
      <ComboboxInput showClear placeholder="Unset" className={cn('w-full', className)}>
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
      </ComboboxInput>
      <ComboboxContent>
        <ComboboxEmpty>
          {isFetching || awaitingDebounce ? 'Searching…' : 'No results'}
        </ComboboxEmpty>
        <ComboboxList>
          {(name: string) => (
            <ComboboxItem key={name} value={name}>
              {name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
